import { CheckCircle, Cancel } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { useTranslations } from 'context/TranslationContext';

import { baseHeader } from 'styles/components/Common';
import {
  evaluationTraceSectionStyle,
  evaluationTraceRowStyle,
  evaluationTraceNumberStyle,
  evaluationTraceContentStyle,
  evaluationTraceNameStyle,
  evaluationTraceStatusStyle,
  evaluationTraceReasonStyle,
  evaluationTraceMatchedStyle,
  evaluationTraceNoMatchStyle,
  matchedIconStyle,
  noMatchIconStyle
} from 'styles/pages/ApprovalRuleSimulatorStyles';

import {
  EvaluationTrace,
  EvaluationTraceMatchStatus
} from '../../../../utils/interfaces/ApprovalSimulator';

export interface EvaluationTraceSectionProps {
  traces: EvaluationTrace[];
}

const getMatchStatusLabel = (
  status: EvaluationTraceMatchStatus,
  translate: (key: string) => string
): string => {
  return status === EvaluationTraceMatchStatus.MATCHED
    ? translate('approvalSimulator.trace.matched')
    : translate('approvalSimulator.trace.noMatch');
};

const getReasonWithConvertedAmount = (trace: EvaluationTrace): string => {
  if (!trace.convertedAmount) {
    return trace.reason;
  }

  const { originalAmount, originalCurrency, convertedAmount, targetCurrency } =
    trace.convertedAmount;
  const convertedText = `${originalAmount} ${originalCurrency} ≈ ${convertedAmount} ${targetCurrency}`;

  if (trace.status === EvaluationTraceMatchStatus.MATCHED) {
    return `${trace.reason} — ${convertedText}`;
  }

  return `${trace.reason} (${convertedText})`;
};

export const EvaluationTraceSection = ({
  traces
}: EvaluationTraceSectionProps) => {
  const { translate } = useTranslations();

  if (traces.length === 0) {
    return null;
  }

  return (
    <Box sx={evaluationTraceSectionStyle}>
      <Typography sx={baseHeader}>
        {translate('approvalSimulator.results.evaluationTrace')}
      </Typography>

      {traces.map((trace, index) => (
        <Box
          key={`trace-${trace.id}`}
          sx={[
            evaluationTraceRowStyle,
            trace.status === EvaluationTraceMatchStatus.MATCHED
              ? evaluationTraceMatchedStyle
              : { ...evaluationTraceNoMatchStyle, bgcolor: '#f9fafb' }
          ]}
        >
          <Typography sx={evaluationTraceNumberStyle}>#{index + 1}</Typography>

          <Box sx={evaluationTraceContentStyle}>
            <Typography sx={evaluationTraceNameStyle}>
              {trace.ruleName}
            </Typography>

            <Typography
              sx={evaluationTraceStatusStyle}
              data-status={trace.status === EvaluationTraceMatchStatus.MATCHED}
            >
              {trace.status === EvaluationTraceMatchStatus.MATCHED ? (
                <CheckCircle sx={matchedIconStyle} />
              ) : (
                <Cancel sx={noMatchIconStyle} />
              )}
              <span style={{ flex: 1, textAlign: 'center' }}>
                {getMatchStatusLabel(trace.status, translate)}
              </span>
            </Typography>

            <Typography sx={evaluationTraceReasonStyle}>
              {getReasonWithConvertedAmount(trace)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
